import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { FormLayout, Form, Button, Banner, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import UserSelect from "features/user-select/user-select";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import { sendIncidentForSignature } from "services/work-security-incidents.service";

interface Props {
  incidentId: string;
  victimUser: string | null;
  defaultManagerUser: string | null;
  onSuccess?: () => void;
}

export default function IncidentSendForSignatureModal({
  incidentId,
  victimUser,
  defaultManagerUser,
  onSuccess,
}: Props) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [manager, setManager] = useState(defaultManagerUser || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!companyId) return;
    if (!manager) {
      setError(t("incident_manager_required", { defaultValue: "Manager is required." }));
      return;
    }
    if (victimUser && manager === victimUser) {
      setError(
        t("incident_manager_same_as_victim", {
          defaultValue: "Manager must be different from the affected employee.",
        })
      );
      return;
    }
    try {
      setSaving(true);
      setError("");
      await sendIncidentForSignature(companyId, incidentId, manager);
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());
      onSuccess?.();
    } catch (err: any) {
      console.error("Failed to send incident for signature", err);
      setError(
        err?.response?.data?.message ||
          t("incident_send_failed", { defaultValue: "Failed to send for signature." })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormLayout>
        {error && <Banner tone="critical">{error}</Banner>}

        <Text as="p" variant="bodyMd">
          {t("incident_send_intro", {
            defaultValue:
              "The act will be sent for click-signature to the affected employee and the selected manager.",
          })}
        </Text>

        <UserSelect
          value={manager}
          onChange={(v: string) => setManager(v)}
          label="incident_manager"
          isRequired
          onlyActivated
          showUserEmail
        />

        <div className="flex justify-end gap-3 mt-4 border-t border-gray-150 pt-4">
          <Button
            onClick={() => {
              dispatch(setIsModalxOpen(false));
              dispatch(setDestroy());
            }}
            disabled={saving}
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving} disabled={!manager}>
            {t("send_for_signature", { defaultValue: "Send for signature" })}
          </Button>
        </div>
      </FormLayout>
    </Form>
  );
}
